/**
 * ADR-0161 §7.2 — the Semantic Designer's template layer, as pure logic.
 *
 * The Designer shows a tenant its own objects plus, beneath them, the standard
 * objects of each connector kind it has. Those template objects are READ-ONLY:
 * not draggable, not editable, and explicitly not infer-schema-able (today's
 * per-object infer would both rewrite `fields` and silently convert a `view`
 * source to a `query` source — a lossy fork, never offered implicitly). The one
 * gesture that forks is "Copy into model", which is deliberate and visible.
 *
 * Everything here is presentation arithmetic over data the hub already
 * layered — deciding what a row LOOKS like, never what serves. The hub's
 * layered union is the authority on what an agent actually sees; if these two
 * ever disagree, this one is wrong.
 *
 * Lives in `lib/` because the console test glob is `*.test.ts` — there is no
 * React test renderer, so Designer logic that needs testing is extracted here
 * rather than left in the route (a standing repo constraint).
 */

export type TemplateObject = {
  name: string;
  /** The kind whose template ships this object. */
  template_kind: string;
};

export type TenantObject = { id: string; name: string };

export type TemplateRowState = "available" | "excluded" | "shadowed";

export type TemplateRow = {
  name: string;
  template_kind: string;
  state: TemplateRowState;
};

const lower = (s: string) => s.toLowerCase();

/**
 * Build the Template group's rows.
 *
 * Three states, and the third is the interesting one:
 *   - `available` — served, shown normally, offers Copy-into-model / Exclude
 *   - `excluded`  — the tenant excluded it; stays LISTED, greyed, offers Restore
 *                   (visible-but-inert; suppression never deletes)
 *   - `shadowed`  — a tenant object of the same name already exists, so the
 *                   template's version does not serve
 *
 * A `shadowed` row is HIDDEN from the group (see `visibleTemplateRows`) because
 * listing it would imply it is available. The tenant object carries the
 * disclosure instead — see `shadowIndicatorFor` — which is what keeps the
 * shadowing from being invisible.
 */
export function buildTemplateRows(
  templateObjects: readonly TemplateObject[],
  tenantObjects: readonly TenantObject[],
  excludedNames: ReadonlySet<string>
): TemplateRow[] {
  const tenantNames = new Set(tenantObjects.map((o) => lower(o.name)));
  return templateObjects.map((t) => {
    const key = lower(t.name);
    const state: TemplateRowState = tenantNames.has(key)
      ? "shadowed"
      : excludedNames.has(key)
        ? "excluded"
        : "available";
    return { name: t.name, template_kind: t.template_kind, state };
  });
}

/** The rows the Template group actually renders — everything except shadowed ones. */
export function visibleTemplateRows(rows: readonly TemplateRow[]): TemplateRow[] {
  return rows.filter((r) => r.state !== "shadowed");
}

/**
 * The muted inspector line on a TENANT object that shadows a template object,
 * or null when it shadows nothing.
 *
 * This is the visibility affordance for a fact that would otherwise be
 * invisible: the tenant sees its own object, the template row is hidden, and
 * without this line nothing anywhere says the standard version was displaced.
 */
export function shadowIndicatorFor(
  tenantObjectName: string,
  templateObjects: readonly TemplateObject[]
): string | null {
  const match = templateObjects.find((t) => lower(t.name) === lower(tenantObjectName));
  return match ? "Shadows a template object of the same name." : null;
}

/** Whether the Template group renders at all — no kinds with templates, no group. */
export function shouldShowTemplateGroup(templateObjects: readonly TemplateObject[]): boolean {
  return templateObjects.length > 0;
}

/**
 * The name a "Copy into model" clone takes.
 *
 * Verbatim — NOT `"<name> (copy)"`. The copy exists to shadow the template
 * object, and shadowing keys on the name: renaming it would leave the template
 * version serving alongside the copy, which is the opposite of what the gesture
 * means. A same-named tenant object is legal precisely because the layered
 * union expects one.
 */
export function copyIntoModelName(templateObjectName: string): string {
  return templateObjectName;
}

/** Row actions, by state. Excluded rows offer only Restore — nothing is ever deleted. */
export function templateRowActions(state: TemplateRowState): Array<"copy" | "exclude" | "restore"> {
  if (state === "excluded") return ["restore"];
  if (state === "available") return ["copy", "exclude"];
  return [];
}

/** The ConfirmDialog copy for excluding a template object. */
export function excludeConfirmCopy(objectName: string): { title: string; detail: string; confirmLabel: string } {
  return {
    title: `Exclude "${objectName}" from this tenant's model?`,
    detail: "Queries will no longer see it. You can restore it here.",
    confirmLabel: "Exclude",
  };
}
